import { useEffect, useMemo, useState } from 'react'
import { PhotoViewer } from '../components/PhotoViewer'
import { useParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { Seg, KV, SelectRow } from '../components/Controls'
import { I } from '../components/Icons'
import { Skeleton, Empty } from '../components/Skeleton'
import { useToast } from '../components/Toast'
import { ContactButtons } from '../components/Contact'
import { useStore } from '../state/store'
import { readDay } from '../state/day'
import { useCached, setCached, fetchCached } from '../lib/cache'
import { useLiveRows, pollEvery } from '../lib/live'
import { loadWebInbox, webPhotoUrls, updateWebItem, markWebPieceIntake, countWebNew, WEB_WAITING, type WebSubmission, type WebMessage, type WebSubStatus } from '../lib/api'
import { LABEL } from '../lib/types'

const S_LABEL: Record<WebSubStatus, string> = { new: 'New', reviewing: 'Reviewing', reached_out: 'Reached out', offer_made: 'Offer made', accepted: 'Accepted', passed: 'Passed' }
const M_LABEL = { new: 'New', replied: 'Replied', closed: 'Closed' } as const
const TOPIC = { product: 'Product question', order: 'Order + shipping', press: 'Press + collabs', other: 'Other', vip: 'VIP access request' } as const
const INTENT = { sell: 'Sell outright', consign: 'Consign', either: 'Open to either' } as const
const PREF: Record<string, string> = { text: 'Text', email: 'Email', whatsapp: 'WhatsApp', instagram: 'Instagram DM' }
const OPEN_S: WebSubStatus[] = ['new', 'reviewing', 'reached_out', 'offer_made']
const ago = (d: string) => {
  const m = Math.round((Date.now() - new Date(d).getTime()) / 60000)
  if (m < 60) return `${Math.max(m, 1)} min ago`
  if (m < 1440) return `${Math.round(m / 60)} h ago`
  const days = Math.round(m / 1440); return `${days} day${days === 1 ? '' : 's'} ago`
}
const dot = (on: boolean) => on ? <span aria-label="New" style={{ width: 10, height: 10, borderRadius: 5, background: '#007aff', flexShrink: 0 }} /> : null

/** Sell / Consign submissions and Contact messages from starchive.la. Status, owner and notes go through web-inbox. */
export function Inbox() {
  const nav = useNav()
  const params = useParams()
  const toast = useToast()
  const { employees, resetDraft, setDraft } = useStore()
  const isOwner = employees.some((e) => e.employee_id === readDay()?.employee_id && e.rights === 'owner' && e.active)
  const X = useCached('webInbox', loadWebInbox, { staleMs: 0, enabled: isOwner })
  const live = useLiveRows('web_submissions', isOwner ? '' : null, () => void X.refresh())
  useLiveRows('web_messages', isOwner ? '' : null, () => void X.refresh())
  useEffect(() => { if (!isOwner) return; const t = setInterval(() => void X.refresh(), pollEvery(live, 30_000, 120_000)); return () => clearInterval(t) }, [live, isOwner])   // eslint-disable-line react-hooks/exhaustive-deps
  const data = X.data
  const [tab, setTab] = useState<'s' | 'm'>(params.kind === 'm' ? 'm' : 's')
  const [show, setShow] = useState<'open' | 'all'>('open')
  const [sel, setSel] = useState<string>(params.id ?? '')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [viewer, setViewer] = useState<{ photos: string[]; start: number; title: string } | null>(null)
  const [urls, setUrls] = useState<Record<string, string>>({})
  const me = readDay()

  const subs = useMemo(() => (data?.subs ?? []).filter((s) => show === 'all' || OPEN_S.includes(s.status)), [data, show])
  const msgs = useMemo(() => (data?.msgs ?? []).filter((m) => show === 'all' || m.status === 'new'), [data, show])
  const cur: WebSubmission | WebMessage | undefined = tab === 's' ? data?.subs.find((s) => s.id === sel) : data?.msgs.find((m) => m.id === sel)
  const newS = (data?.subs ?? []).filter((s) => WEB_WAITING.includes(s.status)).length
  const newM = (data?.msgs ?? []).filter((m) => m.status === 'new').length

  useEffect(() => { setNotes(cur?.staff_notes ?? '') }, [cur?.id])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!cur || cur.kind !== 's') return
    const need = cur.pieces.flatMap((p) => p.photos).filter((p) => !urls[p])
    if (!need.length) return
    webPhotoUrls(need).then((u) => setUrls((o) => ({ ...o, ...u }))).catch((e) => Sentry.captureException(e))
  }, [cur?.id])   // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch: Record<string, unknown>, done?: string) => {
    if (!cur || !me?.employee_id) return
    setBusy(true)
    try {
      const row = await updateWebItem(cur.kind, cur.id, patch, me.employee_id)
      setCached<{ subs: WebSubmission[]; msgs: WebMessage[] }>('webInbox', (d) => d ? {
        subs: d.subs.map((s) => s.id === cur.id && cur.kind === 's' ? { ...s, ...row } as WebSubmission : s),
        msgs: d.msgs.map((m) => m.id === cur.id && cur.kind === 'm' ? { ...m, ...row } as WebMessage : m),
      } : { subs: [], msgs: [] })
      void fetchCached('webNew', countWebNew)
      if (done) toast(done, { kind: 'ok' })
    } catch (e) { Sentry.captureException(e); toast((e as Error).message, { kind: 'bad' }) } finally { setBusy(false) }
  }
  // Opening a New item marks it Reviewing (submissions) so the next person sees someone is on it
  const open = (id: string) => {
    setSel(id)
    const s = tab === 's' ? data?.subs.find((x) => x.id === id) : undefined
    if (s && s.status === 'new' && me?.employee_id) updateWebItem('s', id, { status: 'reviewing' }, me.employee_id).then(() => { void fetchCached('webNew', countWebNew); void X.refresh() }).catch(() => { /* stays New */ })
  }
  const startIntake = async (s: WebSubmission, p: WebSubmission['pieces'][number]) => {
    if (!me?.employee_id) return
    try { await markWebPieceIntake(p.id, me.employee_id) } catch (e) { Sentry.captureException(e) }
    resetDraft()
    setDraft({
      type: s.accepted_as === 'consign' ? 'consigned' : 'bought', from: s.name, brand: p.brand, item: p.item, size: p.size, cond: p.condition,
      note: `Website submission SC-${s.ref_no}${p.asking_cents ? `, asked $${(p.asking_cents / 100).toLocaleString('en-US')}` : ''}`,
    })
    nav('/intake')
  }

  const aside = (
    <>
      <div className="aside__body">
        {!cur ? <div className="empty">Pick a {tab === 's' ? 'submission' : 'message'} to see it.</div>
        : cur.kind === 's' ? (
          <>
            <div className="detail-head"><span className="person__initial" style={{ borderRadius: 10, fontSize: 13 }}>SC</span><b>{cur.name}</b><span style={{ marginLeft: 'auto', color: '#8e8e93' }}>SC-{cur.ref_no}</span></div>
            <ContactButtons name={cur.name} phone={cur.phone} email={cur.email} instagram={cur.instagram} pref={cur.contact_pref} toast={toast}
              subject={`Your Starchive submission SC-${cur.ref_no}`}
              body={`Hi ${cur.name.split(' ')[0]}, this is Starchive about your submission SC-${cur.ref_no} (${cur.pieces.map((p) => p.brand).filter(Boolean).join(', ') || 'your piece'}). `} />
            <div className="card review-rows" style={{ margin: '8px 20px' }}>
              <KV k="Prefers" v={PREF[cur.contact_pref] ?? cur.contact_pref} />
              <KV k="Phone" v={cur.phone || '—'} />
              <KV k="Email" v={cur.email} />
              <KV k="Instagram" v={cur.instagram ? '@' + cur.instagram : '—'} />
              <KV k="City" v={cur.city || '—'} />
              <KV k="Wants to" v={INTENT[cur.intent]} />
              <KV k="Piece is" v={cur.location === 'ship' ? 'Outside NYC, would ship' : 'In NYC, can bring it in'} />
              <KV k="Sent" v={new Date(cur.submitted_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} />
            </div>
            {cur.notes && <div className="hint" style={{ padding: '4px 24px 8px', whiteSpace: 'pre-wrap' }}>“{cur.notes}”</div>}
            {cur.pieces.map((p) => (
              <div key={p.id} className="card" style={{ margin: '12px 20px', padding: 14 }}>
                <div style={{ fontWeight: 700 }}>{p.brand} · {p.item}</div>
                <div className="hint" style={{ padding: '4px 0 10px' }}>{[p.category, p.size && 'Size ' + p.size, LABEL.condLong[p.condition], p.asking_cents ? 'Asks $' + (p.asking_cents / 100).toLocaleString('en-US') : 'No asking price', [p.has_receipt && 'receipt', p.has_box && 'box', p.has_extras && 'extra tags'].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}{p.flaws ? ` · Flaws: ${p.flaws}` : ''}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
                  {p.photos.map((ph, i) => urls[ph]
                    ? <button type="button" key={ph} className="inbox-photo" onClick={() => setViewer({ photos: p.photos.map((x) => urls[x]).filter(Boolean), start: p.photos.filter((x) => urls[x]).indexOf(ph), title: `${p.brand} · ${p.item}` })} aria-label={`Photo ${i + 1}, full screen`}><img src={urls[ph]} alt="" style={{ width: '100%', aspectRatio: '1', height: 'auto', objectFit: 'cover', borderRadius: 8, display: 'block', background: '#f2f2f7' }} /></button>
                    : <div key={ph} style={{ aspectRatio: '1', borderRadius: 8, background: '#f2f2f7' }} />)}
                </div>
                {cur.status === 'accepted' && (p.intake_started_at
                  ? <div className="hint" style={{ paddingTop: 10 }}>Intake started {ago(p.intake_started_at)}.</div>
                  : <button type="button" className="cta cta--center" style={{ marginTop: 12, width: '100%' }} onClick={() => startIntake(cur, p)}>Start intake for this piece</button>)}
              </div>
            ))}
            <div className="card" style={{ margin: '12px 20px' }}>
              <SelectRow id="wst" label="Status" value={cur.status} options={(Object.keys(S_LABEL) as WebSubStatus[]).map((v) => ({ v, label: S_LABEL[v] }))} onChange={(v) => save({ status: v, ...(v === 'accepted' && !cur.accepted_as ? { accepted_as: cur.intent === 'consign' ? 'consign' : 'buy' } : {}) }, 'Status saved')} />
              {cur.status === 'accepted' && <div className="row"><span className="row__label">Taking it as</span><Seg value={cur.accepted_as ?? 'buy'} options={[{ v: 'buy', label: 'Buy' }, { v: 'consign', label: 'Consign' }]} onChange={(v) => save({ accepted_as: v })} /></div>}
              <SelectRow id="wsa" label="Who has it" value={cur.assigned_to ?? ''} placeholder="Nobody yet" options={employees.filter((e) => e.active).map((e) => ({ v: e.name, label: e.name }))} onChange={(v) => save({ assigned_to: v || null }, 'Saved')} />
            </div>
          </>
        ) : (
          <>
            <div className="detail-head"><span className="person__initial">{cur.name[0] ?? '?'}</span><b>{cur.name}</b><span style={{ marginLeft: 'auto', color: '#8e8e93' }}>{TOPIC[cur.topic]}</span></div>
            <ContactButtons name={cur.name} phone={cur.phone} email={cur.email} instagram={cur.instagram} pref="email" toast={toast}
              subject={`Re: ${TOPIC[cur.topic]}${cur.order_no ? ' ' + cur.order_no : ''}`}
              body={`Hi ${cur.name.split(' ')[0]}, this is Starchive replying to your message${cur.order_no ? ` about order ${cur.order_no}` : ''}. `} />
            <div className="card" style={{ margin: '8px 20px', padding: 16, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{cur.message}</div>
            <div className="card review-rows" style={{ margin: '8px 20px' }}>
              <KV k="Email" v={cur.email} />
              <KV k="Phone" v={cur.phone || '—'} />
              <KV k="Instagram" v={cur.instagram ? '@' + cur.instagram : '—'} />
              {cur.order_no && <KV k="Order" v={cur.order_no} />}
              {cur.product_ref && <KV k="Piece" v={/^https?:\/\//.test(cur.product_ref) ? <a href={cur.product_ref} target="_blank" rel="noreferrer">Open listing</a> : cur.product_ref} />}
              <KV k="Sent" v={new Date(cur.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} />
            </div>
            <div className="card" style={{ margin: '12px 20px' }}>
              <div className="row"><span className="row__label">Status</span><Seg value={cur.status} options={(Object.keys(M_LABEL) as (keyof typeof M_LABEL)[]).map((v) => ({ v, label: M_LABEL[v] }))} onChange={(v) => save({ status: v }, 'Status saved')} /></div>
            </div>
          </>
        )}
        {cur && (
          <div style={{ margin: '8px 20px 16px' }}>
            <label htmlFor="wnotes" className="section-title" style={{ display: 'block', padding: '8px 4px' }}>Team notes</label>
            <textarea id="wnotes" className="qa-textarea" style={{ width: '100%', boxSizing: 'border-box' }} rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was offered, what they said, next step" />
            {cur.updated_by && <div className="hint" style={{ padding: '6px 4px' }}>Last change by {employees.find((e) => e.employee_id === cur.updated_by)?.name ?? cur.updated_by}</div>}
          </div>
        )}
      </div>
      <div className="aside__foot">
        {cur && <button type="button" className="cta cta--center" disabled={busy || notes === (cur.staff_notes ?? '')} onClick={() => save({ staff_notes: notes }, 'Notes saved')}>{busy ? 'Saving…' : 'Save notes'}</button>}
      </div>
    </>
  )

  const rows = tab === 's' ? subs : msgs
  if (employees.length && !isOwner) return (
    <Page bar={<TopBar back="Home" backTo="/" title="Inbox" />}>
      <Empty icon={I.inbox(24)} line="The Inbox is for owners. Sign in with an owner ID to open it." />
    </Page>
  )
  return (
    <Page bar={<TopBar back="Home" backTo="/" title="Inbox" caption={newS + newM ? `${newS + newM} waiting` : 'From starchive.la'} />} aside={aside}>
      {X.error && !data && <div className="banner" style={{ background: '#d70015' }}>{X.error}</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0 12px', flexWrap: 'wrap' }}>
        <Seg value={tab} options={[{ v: 's', label: `Sell / consign${newS ? ` (${newS})` : ''}` }, { v: 'm', label: `Messages${newM ? ` (${newM})` : ''}` }]} onChange={(v) => { setTab(v); setSel('') }} />
        <Seg value={show} options={[{ v: 'open', label: 'Open' }, { v: 'all', label: 'All' }]} onChange={setShow} />
      </div>
      <div className="card">
        {!data && !X.error && <Skeleton rows={5} h={64} card={false} />}
        {tab === 's' && subs.map((s) => (
          <button key={s.id} type="button" className={'person' + (s.id === sel ? ' sel' : '')} onClick={() => open(s.id)}>
            {dot(s.status === 'new')}
            <span style={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}><span className="person__name">{s.pieces.map((p) => p.brand).join(', ') || 'Submission'} · {s.name}</span><span className="person__sub">SC-{s.ref_no} · {INTENT[s.intent]} · {s.pieces.length} piece{s.pieces.length === 1 ? '' : 's'} · {S_LABEL[s.status]}{s.assigned_to ? ` · ${s.assigned_to}` : ''} · {ago(s.submitted_at)}</span></span>
            {I.chev()}
          </button>
        ))}
        {tab === 'm' && msgs.map((m) => (
          <button key={m.id} type="button" className={'person' + (m.id === sel ? ' sel' : '')} onClick={() => open(m.id)}>
            {dot(m.status === 'new')}
            <span style={{ flexGrow: 1, minWidth: 0, textAlign: 'left' }}><span className="person__name">{m.name} · {TOPIC[m.topic]}</span><span className="person__sub" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.message}</span><span className="person__sub">{M_LABEL[m.status]} · {ago(m.created_at)}</span></span>
            {I.chev()}
          </button>
        ))}
        {data && rows.length === 0 && <Empty icon={I.inbox(24)} line={show === 'open' ? (tab === 's' ? 'No open submissions. They land here from starchive.la/pages/sell.' : 'No new messages.') : 'Nothing yet.'} />}
      </div>
      {viewer && <PhotoViewer photos={viewer.photos} start={viewer.start} title={viewer.title} onClose={() => setViewer(null)} />}
    </Page>
  )
}
